import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { searchMembers } from "@/api/client";
import { SearchBox } from "@/components/SearchBox";
import { ErrorBanner } from "@/components/ui";

export type PickedMember = { id: string; name: string };

export function MemberPicker({
  value,
  onChange,
  problem,
}: {
  value: PickedMember | null;
  onChange: (member: PickedMember | null) => void;
  problem?: string;
}) {
  const [q, setQ] = useState("");
  const results = useQuery({
    queryKey: ["member-picker", q],
    queryFn: () => searchMembers({ q, limit: 10 }),
    enabled: !value && q !== "",
  });
  const items = results.data?.items ?? [];

  return (
    <div className="text-sm">
      <span className="font-medium text-zinc-700">Member</span>
      {value ? (
        <div className="mt-1 flex items-center gap-3">
          <span className="font-medium text-zinc-950">{value.name}</span>
          <button type="button" onClick={() => onChange(null)} className="text-xs font-medium underline">
            Change
          </button>
        </div>
      ) : (
        <div className="mt-1 space-y-2">
          <div className="flex">
            <SearchBox q={q} onSearch={setQ} ariaLabel="Member" placeholder="Search by name or membership number" />
          </div>
          <ErrorBanner error={results.error} />
          {results.isSuccess && items.length === 0 ? <p className="text-xs text-zinc-600">No members match.</p> : null}
          {items.length > 0 ? (
            <ul className="divide-y divide-zinc-100 rounded-md border border-zinc-200">
              {items.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    onClick={() => onChange({ id: m.id, name: m.full_name })}
                    className="w-full px-3 py-2 text-left hover:bg-zinc-50"
                  >
                    {m.full_name} · {m.membership_no ?? "no number"}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      )}
      {problem ? <p className="mt-1 text-xs text-red-700">{problem}</p> : null}
    </div>
  );
}
